import { TableHeaderCategoryConfig } from "../tableContext/TableContext";

export interface TableColumnMetrics {
  content: number;
  text: number;
  word: number;
  badge: number;
  badgeWord: number;
  padding: number;
  accessory: number;
}

export interface TableMeasuredCell {
  element: HTMLTableCellElement;
  index: number;
  span: number;
}

interface ColumnSize {
  min: number;
  preferred: number;
  max: number;
  grow: number;
}

const number = (value: string) => parseFloat(value) || 0;

// Measure content, rather than the space its current column happens to occupy.
// Canvas keeps wrapping/width changes from feeding back into the next measurement.
export function measureTableColumns(root: HTMLElement, count: number) {
  const canvas = document.createElement("canvas").getContext("2d");
  const styles = new WeakMap<Element, CSSStyleDeclaration>();
  const measuredText = new Map<string, number>();
  const styleOf = (element: Element) => {
    let style = styles.get(element);
    if (!style) {
      style = getComputedStyle(element);
      styles.set(element, style);
    }
    return style;
  };
  const textWidth = (text: string, element: Element) => {
    const style = styleOf(element);
    const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const key = `${font}|${style.letterSpacing}|${text}`;
    const cached = measuredText.get(key);
    if (cached !== undefined) return cached;
    if (canvas) {
      canvas.font = font;
    }
    const width =
      (canvas?.measureText(text).width ??
        text.length * number(style.fontSize) * 0.6) +
      Math.max(0, text.length - 1) * number(style.letterSpacing);
    measuredText.set(key, width);
    return width;
  };
  const metrics = Array.from({ length: count }, () => ({
    content: 0,
    text: 0,
    word: 0,
    badge: 0,
    badgeWord: 0,
    padding: 0,
    accessory: 0,
  }));

  const measure = (element: Element, column: TableColumnMetrics): number => {
    const style = styleOf(element);
    if (
      style.display === "none" ||
      style.position === "absolute" ||
      style.position === "fixed"
    ) {
      return 0;
    }
    const outer =
      number(style.paddingLeft) +
      number(style.paddingRight) +
      number(style.borderLeftWidth) +
      number(style.borderRightWidth);
    const margins = number(style.marginLeft) + number(style.marginRight);
    if (["svg", "IMG", "INPUT"].includes(element.tagName)) {
      return element.getBoundingClientRect().width + margins;
    }
    let inline = 0;
    let widest = 0;
    const isRow =
      (style.display === "flex" || style.display === "inline-flex") &&
      !style.flexDirection.startsWith("column");
    const isColumn = style.flexDirection.startsWith("column");
    let childCount = 0;
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent ?? "").replace(/\s+/g, " ").trim();
        if (!text) return;
        const width = textWidth(text, element);
        const wordWidth = Math.max(
          0,
          ...text.split(/\s+/).map((word) => textWidth(word, element)),
        );
        column.word = Math.max(column.word, wordWidth);
        const badge = element.closest("[data-table-badge]");
        if (badge) {
          const badgeStyle = styleOf(badge);
          column.badgeWord = Math.max(
            column.badgeWord,
            wordWidth +
              number(badgeStyle.paddingLeft) +
              number(badgeStyle.paddingRight) +
              number(badgeStyle.borderLeftWidth) +
              number(badgeStyle.borderRightWidth),
          );
        } else {
          column.text = Math.max(column.text, width);
        }
        inline += width;
        childCount++;
      } else if (node instanceof Element) {
        const childStyle = styleOf(node);
        const width = measure(node, column);
        if (!width) return;
        childCount++;
        if (isRow || (!isColumn && childStyle.display.startsWith("inline"))) {
          inline += width;
        } else {
          widest = Math.max(widest, inline, width);
          inline = 0;
        }
      }
    });
    const gap = isRow
      ? Math.max(0, childCount - 1) * number(style.columnGap)
      : 0;
    const width = Math.max(widest, inline + gap) + outer;
    const badgeMin = element.getAttribute("data-table-badge-min-width") ?? "";
    const preferredMin = badgeMin.endsWith("rem")
      ? number(badgeMin) *
        number(getComputedStyle(document.documentElement).fontSize)
      : badgeMin.endsWith("em")
      ? number(badgeMin) * number(style.fontSize)
      : badgeMin.endsWith("%")
      ? 0
      : number(badgeMin);
    const badgeTextWidth = element.hasAttribute("data-table-badge")
      ? textWidth(
          (element.textContent ?? "").replace(/\s+/g, " ").trim(),
          element,
        ) + outer
      : 0;
    const intrinsic = Math.max(
      width,
      badgeTextWidth,
      number(style.minWidth),
      preferredMin,
    );
    if (element.hasAttribute("data-table-badge")) {
      column.badge = Math.max(column.badge, intrinsic);
    }
    return intrinsic + margins;
  };

  const cells: TableMeasuredCell[] = [];
  root
    .querySelectorAll<HTMLTableElement>("[data-table-layout]")
    .forEach((table) => {
      if (table.parentElement?.closest("[data-table-layout]")) return;
      Array.from(table.tBodies).forEach((body) => {
        const occupied: number[] = [];
        Array.from(body.rows).forEach((row, rowIndex) => {
          let index = 0;
          Array.from(row.cells).forEach((cell) => {
            while (occupied[index] > 0) index++;
            const span = Math.min(cell.colSpan, count - index);
            if (span < 1) return;
            const start = index;
            for (let offset = 0; offset < span; offset++) {
              occupied[index++] =
                cell.rowSpan === 0 ? body.rows.length - rowIndex : cell.rowSpan;
            }
            if (row.hasAttribute("data-table-row"))
              cells.push({ element: cell, index: start, span });
          });
          occupied.forEach((remaining, index) => {
            occupied[index] = Math.max(0, remaining - 1);
          });
        });
      });
    });
  cells.forEach(({ element: cell, index, span }) => {
    // Spanning notes wrap across existing tracks; they must not widen short ID columns.
    if (!metrics[index] || span !== 1) return;
    const style = styleOf(cell);
    const padding = number(style.paddingLeft) + number(style.paddingRight);
    const measured: TableColumnMetrics = {
      content: 0,
      text: 0,
      word: 0,
      badge: 0,
      badgeWord: 0,
      padding,
      accessory: 0,
    };
    const width = measure(cell, measured);
    cell
      .querySelectorAll<HTMLElement>("img, .MuiCheckbox-root")
      .forEach((element) => {
        const accessory =
          element.tagName === "IMG"
            ? element.parentElement ?? element
            : element;
        const accessoryStyle = styleOf(accessory);
        measured.accessory +=
          accessory.getBoundingClientRect().width +
          number(accessoryStyle.marginLeft) +
          number(accessoryStyle.marginRight);
      });
    for (let offset = 0; offset < span; offset++) {
      const column = metrics[index + offset];
      column.content = Math.max(column.content, width / span);
      column.text = Math.max(column.text, measured.text / span);
      column.word = Math.max(column.word, measured.word / span);
      column.badge = Math.max(column.badge, measured.badge / span);
      column.badgeWord = Math.max(column.badgeWord, measured.badgeWord / span);
      column.padding = Math.max(column.padding, padding);
      column.accessory = Math.max(column.accessory, measured.accessory);
    }
  });
  return { metrics, cells };
}

export function getTableColumnLayout({
  headers,
  metrics,
  availableWidth,
  rem,
  showFilters,
  withActions,
  measureLabel,
}: {
  headers: TableHeaderCategoryConfig[];
  metrics: TableColumnMetrics[];
  availableWidth: number;
  rem: number;
  showFilters: boolean;
  withActions: boolean;
  measureLabel: (text: string) => number;
}) {
  const columns: ColumnSize[] = headers.map((header, index) => {
    const metric = metrics[index];
    const padding = metric?.padding || rem * 1.5;
    const badgeMin = metric?.badgeWord
      ? Math.ceil(metric.badgeWord) + padding + (metric.accessory ?? 0)
      : 0;
    // A badge's longest word is indivisible; the table can scroll rather than clip it.
    const max = Math.max(header.minW ?? 0, badgeMin, header.maxW ?? Infinity);
    const controlMin = !showFilters
      ? 0
      : header.withDatePicker
      ? 180
      : header.withSearch || header.withFilter?.length
      ? 124
      : 0;
    const labelWidth =
      measureLabel(header.label ?? header.value) +
      padding +
      (header.withOrder ? 20 + rem * 0.5 : 0);
    const min = Math.min(
      max,
      Math.max(
        header.minW ?? 72,
        badgeMin,
        controlMin,
        Math.min(labelWidth, rem * 8),
        Math.min((metric?.word ?? 0) + padding, rem * 17) +
          (metric?.accessory ?? 0),
        (metric?.text ?? 0) > rem * 20 ? rem * 15 : 0,
      ),
    );
    if (header.width !== undefined) {
      const fixed = Math.min(
        max,
        Math.max(header.minW ?? 0, badgeMin, header.width),
      );
      return { min: fixed, preferred: fixed, max: fixed, grow: 0 };
    }
    return {
      min,
      preferred: Math.min(
        max,
        Math.max(
          min,
          Math.min(labelWidth, rem * 15),
          Math.min(metric?.content ?? 0, rem * 32),
        ),
      ),
      max,
      grow: Math.max(0, Math.min(metric?.text ?? 0, rem * 32) - min + padding),
    };
  });
  if (withActions) {
    const width = Math.max(metrics[headers.length]?.content ?? 0, 92);
    columns.push({ min: width, preferred: width, max: width, grow: 0 });
  }
  const widths = columns.map(({ min }) => min);
  let remaining = Math.max(
    0,
    availableWidth - widths.reduce((a, b) => a + b, 0),
  );
  const demand = columns.map(({ preferred, min }) => preferred - min);
  const totalDemand = demand.reduce((a, b) => a + b, 0);
  if (totalDemand) {
    const budget = Math.min(remaining, totalDemand);
    widths.forEach((_, index) => {
      widths[index] += (budget * demand[index]) / totalDemand;
    });
    remaining -= budget;
  }
  const distribute = (weighted: boolean) => {
    let candidates = columns
      .map((_, index) => index)
      .filter(
        (index) =>
          widths[index] < columns[index].max &&
          (!weighted || columns[index].grow > 0),
      );
    while (remaining > 0.5 && candidates.length) {
      const total = candidates.reduce(
        (sum, index) => sum + (weighted ? columns[index].grow : 1),
        0,
      );
      const budget = remaining;
      candidates.forEach((index) => {
        const extra = Math.min(
          columns[index].max - widths[index],
          (budget * (weighted ? columns[index].grow : 1)) / total,
        );
        widths[index] += extra;
        remaining -= extra;
      });
      candidates = candidates.filter(
        (index) => widths[index] < columns[index].max,
      );
    }
  };
  distribute(true);
  distribute(false);
  return {
    widths,
    // An empty track keeps hard width/maxW limits even if every column is bounded.
    fillerWidth: remaining > 0.5 ? remaining : 0,
    width: Math.max(
      availableWidth,
      widths.reduce((a, b) => a + b, 0),
    ),
  };
}
